import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { ChevronsRight } from "lucide-react";
import t from "@/lang/lang";
import {
  ArtefactKind,
  ArtefactKindList,
  artefactKindsUpTo,
} from "@/services/backend/artefacts/kind";
import { Process } from "@/services/backend/processes/process/process";
import { useProcessesListQuery } from "@/services/backend/processes/process/service";
import { Label } from "@/services/backend/processes/state/state";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { ServiceObjectName } from "@/shared/components/domain/customer/service-object-name";
import { MilestoneLabel } from "@/shared/components/domain/processes/milestone-label";
import { processMilestoneLink } from "@/shared/components/domain/processes/process-milestone-link";
import { ProcessNumber } from "@/shared/components/domain/processes/process-number";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Skeleton } from "@/shared/components/ui/skeleton";

const listLimit = 20;

/**
 * NextProcessDropdown lists other running processes which sit on the
 * currently viewed milestone or on any milestone before it, so the user
 * can keep working on the same milestone across processes. Processes which
 * are no longer running (done, archived) are excluded.
 */
export function NextProcessDropdown({ process }: { process: Process }) {
  const [open, setOpen] = useState(false);
  const anchor = useViewedMilestone(process);

  const {
    data: list,
    isLoading,
    error,
  } = useProcessesListQuery(
    {
      currentMilestone: { active: true, values: artefactKindsUpTo(anchor) },
      state: { active: true, values: [Label.Running] },
      // oldest first, the dropdown is a FIFO work queue
      orderBy: [{ field: "createdAt", dsc: false }],
      limit: listLimit,
      offset: 0,
    },
    { skip: !open, refetchOnMountOrArgChange: true },
  );

  // the process id is not part of the list request, so the process the user
  // is currently looking at has to be dropped here
  const candidates = (list?.items ?? []).filter((p) => p.id !== process.id);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <ChevronsRight className="mr-2 h-4 w-4" />
          <span>{t("Nächster Vorgang")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-96 w-72" align="end">
        {isLoading && <LoadingItems />}
        {!isLoading && error != null && (
          <DropdownMenuItem disabled>
            {t("Vorgänge konnten nicht geladen werden")}
          </DropdownMenuItem>
        )}
        {!isLoading && error == null && candidates.length === 0 && (
          <DropdownMenuItem disabled>
            {t("Keine offenen Vorgänge")}
          </DropdownMenuItem>
        )}
        {!isLoading && error == null && candidates.length > 0 && (
          <MilestoneGroups anchor={anchor} processes={candidates} />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * MilestoneGroups renders one group per milestone, starting with the anchor
 * milestone followed by the preceding milestones. Groups are separated by a
 * DropdownMenuSeparator, empty groups are omitted.
 */
function MilestoneGroups({
  anchor,
  processes,
}: {
  anchor: ArtefactKind;
  processes: Process[];
}) {
  const groups = artefactKindsUpTo(anchor)
    .reverse()
    .map((kind) => ({
      kind,
      items: processes.filter((p) => p.currentMilestone === kind),
    }))
    .filter((group) => group.items.length > 0);

  return groups.map((group, idx) => (
    <DropdownMenuGroup key={group.kind}>
      {idx > 0 && <DropdownMenuSeparator />}
      <DropdownMenuLabel className="text-muted-foreground text-xs">
        <MilestoneLabel status={group.kind} />
      </DropdownMenuLabel>
      {group.items.map((item) => (
        <DropdownMenuItem key={item.id} asChild>
          <NavLink to={processMilestoneLink(item)}>
            <div className="flex w-full flex-col gap-0.5 overflow-hidden">
              <span className="flex items-baseline gap-1 truncate font-mono font-bold">
                <ProcessNumber processId={item.id} />
                <span className="text-muted-foreground truncate font-sans font-normal">
                  — <ServiceObjectName serviceObjectId={item.serviceObjectId} />
                </span>
              </span>
              <span className="text-muted-foreground truncate text-xs">
                <CustomerName customerId={item.customerId} />
              </span>
            </div>
          </NavLink>
        </DropdownMenuItem>
      ))}
    </DropdownMenuGroup>
  ));
}

function LoadingItems() {
  return (
    <div className="space-y-2 p-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * useViewedMilestone derives the milestone the user is looking at from the
 * route segment following the process id. On routes which are not a milestone,
 * e.g. overview or contact, the current milestone of the process is used.
 */
function useViewedMilestone(process: Process): ArtefactKind {
  const location = useLocation();

  const idx = location.pathname.indexOf(process.id);
  const segment = location.pathname
    .slice(idx + process.id.length + 1)
    .split("/")[0];

  return ArtefactKindList.includes(segment as ArtefactKind)
    ? (segment as ArtefactKind)
    : (process.currentMilestone as ArtefactKind);
}
